import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsAPI } from '../lib/api';
import { formatCurrency as fmtCurrency, formatDateOnly as fmtDateOnly, formatDate as fmtDateTime } from '../lib/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import PaymentBreakdownPanel from '../components/reports/PaymentBreakdownPanel';
import { BRAND_ASSETS } from '../config/brandAssets';
import { 
    FileText, 
    Calendar as CalendarIcon, 
    Download, 
    Loader2,
    Banknote,
    CreditCard,
    Building2,
    AlertCircle,
    Printer,
    FileCheck,
    MoreHorizontal
} from 'lucide-react';

const LOGO_URL = BRAND_ASSETS.LOGO_WORDMARK;

const Reports = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);

    const generateReport = async () => {
        if (!startDate || !endDate) {
            toast.error(t('entities:reports.toasts.select_dates'));
            return;
        }

        if (startDate > endDate) {
            toast.error(t('entities:reports.toasts.start_before_end'));
            return;
        }

        setLoading(true);
        try {
            const response = await reportsAPI.get(
                format(startDate, 'yyyy-MM-dd'),
                format(endDate, 'yyyy-MM-dd')
            );
            setReportData(response.data);
        } catch (error) {
            toast.error(t('entities:reports.toasts.generate_failed'));
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => fmtCurrency(value, i18n.language);

    const formatDate = (dateStr) => fmtDateOnly(dateStr, i18n.language);

    const getPaymentBadge = (method, isPaid = true) => {
        const colorMap = {
            cash: 'badge-cash',
            card: 'badge-card',
            bank_transfer: 'badge-bank',
            cheque: 'bg-[#8B5CF6] text-white',
            due: isPaid ? 'bg-[#2A9D8F] text-white' : 'badge-due',
            others: 'bg-[#6B7280] text-white',
        };

        const labelMap = {
            cash: t('entities:reports.methods.cash'),
            card: t('entities:reports.methods.card'),
            bank_transfer: t('entities:reports.methods.bank'),
            cheque: t('entities:reports.methods.cheque'),
            due: isPaid ? t('entities:reports.methods.due_paid') : t('entities:reports.methods.due'),
            others: t('entities:reports.methods.others'),
        };

        return (
            <Badge className={`${colorMap[method] || 'bg-gray-500 text-white'} font-medium`}>
                {labelMap[method] || method}
            </Badge>
        );
    };

    const exportToCSV = () => {
        if (!reportData) return;

        const headers = [
            t('entities:reports.columns.invoice'),
            t('entities:reports.columns.company'),
            t('entities:reports.columns.amount'),
            t('entities:reports.columns.method'),
            t('entities:reports.columns.date'),
            t('entities:reports.columns.status'),
            t('entities:reports.columns.recorded_by'),
        ];
        const rows = reportData.sales.map(sale => [
            sale.invoice_number,
            sale.company_name || '',
            sale.amount,
            sale.payment_method,
            format(new Date(sale.created_at), 'yyyy-MM-dd'),
            sale.payment_method === 'due'
                ? (sale.is_due_paid ? t('entities:reports.methods.due_paid') : t('entities:reports.methods.due'))
                : t('entities:reports.methods.due_paid'),
            sale.created_by_name
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('entities:reports.toasts.exported'));
    };

    const handlePrint = () => {
        window.print();
    };

    const setPresetRange = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setStartDate(start);
        setEndDate(end);
    };

    // Calculate cheque total from sales
    const calculateChequeTotal = () => {
        if (!reportData) return 0;
        return reportData.sales
            .filter(sale => sale.payment_method === 'cheque')
            .reduce((sum, sale) => sum + sale.amount, 0);
    };

    // Calculate others total
    const calculateOthersTotal = () => {
        if (!reportData) return 0;
        return reportData.sales
            .filter(sale => sale.payment_method === 'others')
            .reduce((sum, sale) => sum + sale.amount, 0);
    };

    return (
        <div data-testid="reports-page" className="space-y-8 animate-fadeIn">
            {/* Header - Hidden on print */}
            <div className="no-print">
                <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:reports.title')}</h1>
                <p className="text-gray-500 mt-1">{t('entities:reports.subtitle')}</p>
            </div>

            {/* Filter Section - Hidden on print */}
            <Card className="border-0 shadow-sm no-print">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="text-[#C08478]" size={20} />
                        {t('entities:reports.generate_card_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap items-end gap-4">
                        {/* Date Range Pickers */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t('entities:reports.start_date')}</label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        data-testid="start-date-picker"
                                        className="w-48 justify-start text-left font-normal"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {startDate ? format(startDate, 'dd MMM yyyy') : t('entities:reports.select_date')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={startDate}
                                        onSelect={setStartDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t('entities:reports.end_date')}</label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        data-testid="end-date-picker"
                                        className="w-48 justify-start text-left font-normal"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {endDate ? format(endDate, 'dd MMM yyyy') : t('entities:reports.select_date')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={endDate}
                                        onSelect={setEndDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Quick Presets */}
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setPresetRange(0)}
                                className="text-sm"
                            >
                                {t('entities:reports.presets.today')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setPresetRange(7)}
                                className="text-sm"
                            >
                                {t('entities:reports.presets.last_7')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setPresetRange(30)}
                                className="text-sm"
                            >
                                {t('entities:reports.presets.last_30')}
                            </Button>
                        </div>

                        <Button
                            data-testid="generate-report-btn"
                            onClick={generateReport}
                            disabled={loading}
                            className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-6"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    {t('entities:reports.generating')}
                                </>
                            ) : (
                                t('entities:reports.generate')
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Report Results */}
            {reportData && (
                <>
                    {/* Print Header - Only visible on print */}
                    <div className="print-only hidden">
                        <div className="print-header">
                            <img src={LOGO_URL} alt="UNIVERS COSMETIC" className="print-logo" />
                            <div className="print-title">
                                <h1>UNIVERS COSMETIC</h1>
                                <h2>{t('entities:reports.print_report_title')}</h2>
                                <p>{format(startDate, 'dd MMM yyyy')} - {format(endDate, 'dd MMM yyyy')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 print-summary">
                        <Card className="border-0 shadow-sm print-card">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Banknote className="text-[#2A9D8F]" size={16} />
                                    <span className="text-xs text-gray-500">{t('entities:reports.methods.cash')}</span>
                                </div>
                                <p className="text-lg font-bold text-[#1A1A1A] euro-amount">
                                    {formatCurrency(reportData.summary.total_cash)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-sm print-card">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <CreditCard className="text-[#264653]" size={16} />
                                    <span className="text-xs text-gray-500">{t('entities:reports.methods.card')}</span>
                                </div>
                                <p className="text-lg font-bold text-[#1A1A1A] euro-amount">
                                    {formatCurrency(reportData.summary.total_card)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-sm print-card">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Building2 className="text-[#E9C46A]" size={16} />
                                    <span className="text-xs text-gray-500">{t('entities:reports.methods.bank')}</span>
                                </div>
                                <p className="text-lg font-bold text-[#1A1A1A] euro-amount">
                                    {formatCurrency(reportData.summary.total_bank_transfer)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-sm print-card">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <FileCheck className="text-[#8B5CF6]" size={16} />
                                    <span className="text-xs text-gray-500">{t('entities:reports.methods.cheque')}</span>
                                </div>
                                <p className="text-lg font-bold text-[#1A1A1A] euro-amount">
                                    {formatCurrency(reportData.summary.total_cheque || calculateChequeTotal())}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-sm print-card">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <AlertCircle className="text-[#E76F51]" size={16} />
                                    <span className="text-xs text-gray-500">{t('entities:reports.methods.due')}</span>
                                </div>
                                <p className="text-lg font-bold text-[#E76F51] euro-amount">
                                    {formatCurrency(reportData.summary.total_due)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-sm print-card">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <MoreHorizontal className="text-[#6B7280]" size={16} />
                                    <span className="text-xs text-gray-500">{t('entities:reports.methods.others')}</span>
                                </div>
                                <p className="text-lg font-bold text-[#1A1A1A] euro-amount">
                                    {formatCurrency(reportData.summary.total_others || calculateOthersTotal())}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-sm bg-[#1A1A1A] print-card print-total">
                            <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs text-gray-300">{t('entities:reports.methods.grand_total')}</span>
                                </div>
                                <p className="text-lg font-bold text-white euro-amount">
                                    {formatCurrency(reportData.summary.grand_total)}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Payment Breakdown Panel — dedicated, at-a-glance summary
                        of every payment method with percentage of Total Paid.
                        Backend handles split-payment aggregation via
                        `_payment_amounts()` in routes/dashboard.py. */}
                    <PaymentBreakdownPanel summary={reportData.summary} />

                    {/* Export Buttons - Hidden on print */}
                    <div className="flex gap-3 no-print">
                        <Button
                            data-testid="export-csv-btn"
                            onClick={exportToCSV}
                            variant="outline"
                            className="rounded-full"
                        >
                            <Download size={16} className="mr-2" />
                            {t('common:actions.export_csv')}
                        </Button>
                        <Button
                            onClick={handlePrint}
                            variant="outline"
                            className="rounded-full"
                        >
                            <Printer size={16} className="mr-2" />
                            {t('entities:reports.print_pdf')}
                        </Button>
                    </div>

                    {/* Sales Table */}
                    <Card className="border-0 shadow-sm print-table-card">
                        <CardHeader className="no-print">
                            <CardTitle>
                                {t('entities:reports.details_title', { count: reportData.sales.length })}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="print-table-content">
                            {reportData.sales.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                    <FileText size={48} className="mb-3 opacity-50" />
                                    <p>{t('entities:reports.empty')}</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="print-table w-full">
                                        <thead>
                                            <tr className="bg-[#F5F5F5] print-table-header">
                                                <th className="font-medium text-left p-3">{t('entities:reports.columns.invoice')}</th>
                                                <th className="font-medium text-left p-3">{t('entities:reports.columns.company')}</th>
                                                <th className="font-medium text-left p-3">{t('entities:reports.columns.amount')}</th>
                                                <th className="font-medium text-left p-3">{t('entities:reports.columns.method')}</th>
                                                <th className="font-medium text-left p-3">{t('entities:reports.columns.date')}</th>
                                                <th className="font-medium text-left p-3">{t('entities:reports.columns.recorded_by')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.sales.map((sale) => (
                                                <tr key={sale.id} className="print-table-row border-b border-gray-100">
                                                    <td className="p-3 font-mono text-sm font-medium">
                                                        {sale.invoice_number}
                                                    </td>
                                                    <td className="p-3 text-gray-700">
                                                        {sale.company_name || '-'}
                                                    </td>
                                                    <td className="p-3 font-semibold euro-amount">
                                                        {formatCurrency(sale.amount)}
                                                    </td>
                                                    <td className="p-3">
                                                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                            sale.payment_method === 'cash' ? 'bg-[#2A9D8F] text-white' :
                                                            sale.payment_method === 'card' ? 'bg-[#264653] text-white' :
                                                            sale.payment_method === 'bank_transfer' ? 'bg-[#E9C46A] text-black' :
                                                            sale.payment_method === 'cheque' ? 'bg-[#8B5CF6] text-white' :
                                                            sale.payment_method === 'due' ? 'bg-[#E76F51] text-white' :
                                                            'bg-[#6B7280] text-white'
                                                        }`}>
                                                            {sale.payment_method === 'bank_transfer'
                                                                ? t('entities:reports.methods.bank')
                                                                : sale.payment_method === 'cash' ? t('entities:reports.methods.cash')
                                                                : sale.payment_method === 'card' ? t('entities:reports.methods.card')
                                                                : sale.payment_method === 'cheque' ? t('entities:reports.methods.cheque')
                                                                : sale.payment_method === 'due' ? t('entities:reports.methods.due')
                                                                : sale.payment_method === 'others' ? t('entities:reports.methods.others')
                                                                : sale.payment_method}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-gray-600">
                                                        {formatDate(sale.created_at)}
                                                    </td>
                                                    <td className="p-3 text-gray-600">
                                                        {sale.created_by_name}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Print Footer */}
                    <div className="print-only hidden print-footer">
                        <p>{t('entities:reports.generated_on', { when: fmtDateTime(new Date().toISOString(), i18n.language) })}</p>
                        <p>{t('entities:reports.print_footer')}</p>
                    </div>
                </>
            )}
        </div>
    );
};

export default Reports;
